import React, { createContext, useEffect, useContext, useState } from 'react';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserStore } from '../store/userStore';
import apiClient from '../lib/api/apiClient';
import { useUser } from '../hooks/useUser';
import { UserLeagueAffiliation } from '../hooks/useUser';

interface UserContextType {
  currentLeague: UserLeagueAffiliation | null;
  myTeam: number | null;
  systemSettings: { [key: string]: string } | null;
  switchToLeague: (leagueId: number) => void;
  switchToTeam: (teamId: number) => Promise<void>;
  leagueAffiliations: UserLeagueAffiliation[];
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {

  const { leagueAffiliations, isLoading } = useUser();
  const { user, setMyTeam } = useUserStore();
  const setMyLeague = useMyLeagueStore(state => state.setMyLeague);

  // Local state for myTeam to ensure immediate updates
  const [localMyTeam, setLocalMyTeam] = useState<number | null>(user?.user_data?.myTeam || null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Sync local state with user store
  useEffect(() => {
    if (user?.user_data?.myTeam !== localMyTeam) {
      setLocalMyTeam(user?.user_data?.myTeam || null);
    }
  }, [user?.user_data?.myTeam, localMyTeam]);

  // Find the current league based on localMyTeam - everything is built around myTeam
  // Use Number() to ensure both values are numbers for comparison
  const currentLeague = leagueAffiliations?.find((r) => Number(r.team_id) === Number(localMyTeam)) || null;

  // Force recalculation of currentLeague when leagueAffiliations becomes available
  useEffect(() => {
    if (leagueAffiliations && leagueAffiliations.length > 0 && localMyTeam) {
      // Mark that data is loaded to force a re-render
      setDataLoaded(true);
    }
  }, [leagueAffiliations, localMyTeam]);

  // Fetch detailed league data when currentLeague changes
  useEffect(() => {
    const fetchLeagueData = async () => {
      if (currentLeague) {
        try {
          const response = await apiClient.get(`/leagues/populate/${currentLeague.league_id}`);
          setMyLeague(response.data.league);
        } catch (error) {
          console.error('Error fetching league data:', error);
        }
      }
    };

    fetchLeagueData();
  }, [currentLeague, setMyLeague, dataLoaded]);
  
  const switchToLeague = (leagueId: number) => {
    // Find the league and switch to it
    const newLeague = leagueAffiliations?.find(l => l.league_id === leagueId);
    if (newLeague?.team_id) {
      // Switch to the team in this league
      setLocalMyTeam(newLeague.team_id); // Update local state immediately
      setMyTeam(newLeague.team_id); // Update database
    }
  };

  const switchToTeam = async (teamId: number) => {
    console.log('[UserContext] switchToTeam called with teamId:', teamId);
    console.log('[UserContext] Current localMyTeam:', localMyTeam);
    
    // Update local state immediately
    setLocalMyTeam(teamId);
    console.log('[UserContext] Updated localMyTeam to:', teamId);
    
    // Update the database to keep things in sync
    try {
      await setMyTeam(teamId);
      console.log('[UserContext] Successfully updated database with teamId:', teamId);
    } catch (error) {
      console.error('Failed to sync team change to database:', error);
      // Revert local state on error
      setLocalMyTeam(user?.user_data?.myTeam || null);
    }
  };

  if (isLoading) {
    return null; 
  }

  return (
    <UserContext.Provider value={{ 
      currentLeague, 
      myTeam: localMyTeam, 
      systemSettings: user?.systemSettings || null,
      switchToLeague, 
      switchToTeam, 
      leagueAffiliations: leagueAffiliations ?? [] 
    }}>
      {children}
    </UserContext.Provider>
  );
}; 